<!--
A group checkboxes on which all vee validation rules can be applied throught the "rules" prop.
See vee-validationrules.ts for list of available rules

Example use : 
 <GroupCheckboxesWithValidation
    rules="required"
    :label="$t('input-email')"
    placeholder="e.g. EXAMPLE VALUE"
    v-model="strings" => array of string from data including native-values of the checkboxes that should be initially checked
    :data="data" => array of string for all the native-value of checkboxes
    :checkBoxLabels="labels" => array of string for checkboxes label. If not filled, the data values will be taken.
  />
-->
<template>
  <ValidationProvider
    v-slot="{ errors }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="String($attrs.name || $attrs.label || '')"
    :rules="rulesWithoutRequiredRulesIfValidationDisabled"
    :model-value="innerValue"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-danger': showValidationErrors && errors[0],
      }"
      :class="{ 'vertical-checkboxes': verticalDisplay }"
    >
      <template #label>
        <FormInputLabel
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :label="label"
          :is-mandatory="isMandatory"
          :disabled="disabled"
          :input-notice="inputNotice"
        />
      </template>

      <div v-for="(option, i) in data" :key="option" class="field">
        <b-checkbox v-model="innerValue" :native-value="option" :disabled="disabled">
          <span>{{
            checkBoxLabels && checkBoxLabels.length !== 0 ? checkBoxLabels[i] : option
          }}</span>
        </b-checkbox>
      </div>

      <div v-if="Boolean(inputLabel)" class="field GroupCheckboxesWithValidation-inputField">
        <b-checkbox v-model="innerValue" :native-value="inputLabel" :disabled="disabled">
          <span>{{ inputLabel }}</span>
        </b-checkbox>

        <ValidationProvider
          v-slot="{ errors: inputValidationErrors }"
          :vid="(group != '' ? group + '_' : '') + vid + '_checkboxInput'"
          :name="inputLabel"
          :rules="`${validationActivated && inputType === 'is-danger' ? 'required' : ''}`"
          class="GroupCheckboxesWithValidation-inputProvider"
          :model-value="inputValue"
          @update:model-value="updateInputValue"
        >
          <b-field :type="inputType">
            <b-input
              v-model="inputValue"
              type="textarea"
              :placeholder="inputPlaceholder"
              :disabled="disabled || !innerValue.includes(inputLabel)"
              :maxlength="1400"
              @update:model-value="onInputChange"
            ></b-input>
          </b-field>
          <p v-if="showValidationErrors && inputValidationErrors.length > 0" class="help is-danger">
            {{ inputValidationErrors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </b-field>
    <p v-if="showValidationErrors && errors.length > 0" class="help is-danger">
      <span v-if="errorMessage.length > 0"> {{ errorMessage }}</span
      ><span v-else-if="showValidationErrors">
        {{ errors[0] }}
      </span>
    </p>
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "@/views/inputs/FormInputLabel.vue"
import { BInput, BField, BCheckbox } from "buefy"

export default defineComponent({
  name: "GroupCheckboxesWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BInput,
    BField,
    BCheckbox,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    verticalDisplay: Boolean,
    rules: {
      type: [String, Array],
      default: "",
      required: false,
    },
    inputNotice: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    checkBoxLabels: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    data: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    inputLabel: {
      type: String,
      default: "",
    },
    extInputValue: {
      type: String,
      default: "",
    },
    inputPlaceholder: {
      type: String,
      default: "",
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["update:modelValue", "manual-change", "input-change"],
  data() {
    return {
      innerValue: [] as string[],
      inputValue: "",
      inputType: "",
      isMandatory: false,
      rulesWithoutRequiredRulesIfValidationDisabled: "",
    }
  },
  watch: {
    innerValue(newVal: string[], _oldValue: string[]): void {
      this.inputType = ""
      if (Boolean(this.inputLabel) && this.innerValue.includes(this.inputLabel)) {
        this.inputType = this.inputValue ? "is-success" : "is-danger"
      }

      if (this.inputLabel && !this.innerValue.includes(this.inputLabel)) {
        this.inputValue = ""
      }

      // Émettre uniquement update:modelValue, pas manual-change ici pour éviter la boucle
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: string[], _oldValue: string[]): void {
      // Mettre à jour innerValue silencieusement sans émettre d'événements
      if (JSON.stringify(newVal) !== JSON.stringify(this.innerValue)) {
        this.innerValue = newVal || []
      }
    },
    extInputValue(newVal: string): void {
      if (newVal !== this.inputValue) {
        this.inputValue = newVal || ""
      }
    },
    rules(): void {
      this.onRulesChanged()
    },
    validationActivated(): void {
      this.onRulesChanged()
    },
  },
  created(): void {
    if (this.modelValue) {
      this.innerValue = this.modelValue
    }
    if (this.extInputValue) {
      this.inputValue = this.extInputValue
    }
    // Handle both string and array formats for rules in created
    if (this.rules) {
      if (Array.isArray(this.rules)) {
        this.isMandatory = this.rules.includes("required")
      } else {
        this.isMandatory = this.rules.includes("required")
      }
    } else {
      this.isMandatory = false
    }
    this.onRulesChanged()
  },
  methods: {
    onInputChange(): void {
      this.inputType = this.inputValue ? "is-success" : "is-danger"
      this.$emit("input-change", this.inputValue)
    },
    onRulesChanged(): void {
      // Handle both string and array formats for rules
      if (this.rules) {
        if (Array.isArray(this.rules)) {
          this.isMandatory = this.rules.includes("required")
        } else {
          // String format like "required|email"
          this.isMandatory = this.rules.includes("required")
        }
      } else {
        this.isMandatory = false
      }

      if (this.rules) {
        this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules.toString()
        if (!this.validationActivated) {
          this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules
            .toString()
            .replace("required|", "")
            .replace("|required", "")
            .replace("required", "")
        }
      } else {
        this.rulesWithoutRequiredRulesIfValidationDisabled = ""
      }
    },

    updateValue(newValue: string[]): void {
      this.innerValue = newValue
      // Émettre les événements pour une vraie interaction utilisateur
      this.$emit("update:modelValue", newValue)
      this.$emit("manual-change", newValue)
    },

    updateInputValue(newValue: string): void {
      this.inputValue = newValue
      // Trigger les événements nécessaires
      this.onInputChange()
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

// FIXME AL 16/12/20 : Dirty, dirty, Dirty
.vertical-checkboxes .field.has-addons {
  flex-direction: column;
}

.GroupCheckboxesWithValidation-inputProvider {
  width: 100%;
}

#app [disabled].checkbox {
  color: $text-default-color;
}
</style>
